import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import ClickableFeatureTag from '../shared/ClickableFeatureTag';
import TaskDueDate from '../shared/TaskDueDate';
import TaskNotifications from '../shared/TaskNotifications';
import './UnpickedTasksByResource.css';

const UnpickedTasksByResource = ({ unpickedTasksByResource, projectId, onTaskClick, variant, allFeatureTags = [], allTasks = [] }) => {
    const isReport = variant === 'report';
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [expandedCards, setExpandedCards] = useState(new Set());

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const getDaysColor = (days) => {
        return 'info';
        if (days > 3) return 'red';
        if (days >= 1) return 'yellow';
        return 'green';
    };

    const formatDays = (days) => {
        if (days === 0) return 'Today';
        if (days === 1) return '1 day ago';
        return `${days} days ago`;
    };

    const toggleCard = (userId) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const isCardCollapsed = (userId) => {
        // Cards are collapsed by default (not in expandedCards set)
        return !expandedCards.has(userId);
    };

    // Calculate total unpicked tasks across all resources
    const totalUnpickedTasks = unpickedTasksByResource?.reduce((total, resource) => {
        return total + (resource.unpickedCount || 0);
    }, 0) || 0;

    if (!unpickedTasksByResource || unpickedTasksByResource.length === 0) {
        return (
            <div className="unpicked-tasks-section">
                <div 
                    className="section-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <h2>Unpicked Tasks by Resource</h2>
                        <Badge variant="warning">
                            {totalUnpickedTasks} {totalUnpickedTasks === 1 ? 'task' : 'tasks'}
                        </Badge>
                    </div>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="empty-state success">
                        <p>✅ All tasks picked up!</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`unpicked-tasks-section ${isReport ? 'unpicked-tasks-section-report' : ''}`}>
            <div 
                className="section-header" 
                onClick={() => setIsCollapsed(!isCollapsed)} 
                style={{ cursor: 'pointer' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <h2>Unpicked Tasks by Resource</h2>
                    <Badge variant="warning">
                        {totalUnpickedTasks} {totalUnpickedTasks === 1 ? 'task' : 'tasks'}
                    </Badge>
                </div>
                <button 
                    className="stats-toggle-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsCollapsed(!isCollapsed);
                    }}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
            </div>
            <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="unpicked-resources-grid">
                {unpickedTasksByResource.map((resource) => {
                    const cardCollapsed = isCardCollapsed(resource.userId);
                    return (
                        <div
                            key={resource.userId}
                            className={`resource-card ${resource.needsAttention ? 'needs-attention' : ''} ${isReport ? 'resource-card-report' : ''}`}
                        >
                            <div 
                                className="resource-header" 
                                onClick={() => toggleCard(resource.userId)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="resource-info">
                                    <Avatar size="medium">
                                        {getInitials(resource.name)}
                                    </Avatar>
                                    <div className="resource-details">
                                        <div className="resource-name">{resource.name}</div>
                                        <div className="resource-email">{resource.email}</div>
                                        <div className="resource-leave-badges">
                                            {resource.onLeave && (
                                                <Badge variant="error" className="on-leave-badge">
                                                    On Leave
                                                </Badge>
                                            )}
                                            {resource.upcomingLeave?.hasUpcomingLeave && resource.upcomingLeave?.startDate && (() => {
                                                const leaveDate = new Date(resource.upcomingLeave.startDate);
                                                const formattedDate = leaveDate.toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric'
                                                });
                                                return (
                                                    <Badge variant="warning" className="upcoming-leave-badge">
                                                        Next Leave: {formattedDate}
                                                    </Badge>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Badge
                                        variant={resource.unpickedCount > 0 ? 'warning' : 'success'}
                                    >
                                        {resource.unpickedCount} {resource.unpickedCount === 1 ? 'task' : 'tasks'} not picked up
                                    </Badge>
                                    <button 
                                        className="resource-card-toggle-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCard(resource.userId);
                                        }}
                                        aria-label={cardCollapsed ? "Expand tasks" : "Collapse tasks"}
                                    >
                                        {cardCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                    </button>
                                </div>
                            </div>
                            <div className={`resource-card-content ${cardCollapsed ? 'collapsed' : ''}`}>
                                <div className="unpicked-tasks-list">
                            {resource.unpickedTasks?.map((task) => (
                                <div
                                    key={task.taskId}
                                    className={`unpicked-task-item ${!isReport && onTaskClick ? 'unpicked-task-item-clickable' : ''}`}
                                    onClick={!isReport && onTaskClick ? () => onTaskClick({
                                        id: task.taskId,
                                        title: task.title,
                                        display_id: task.displayId,
                                        description: task.description,
                                        status: task.status,
                                        feature_tag: task.feature_tag,
                                        notification: task.notification,
                                        daysSinceAssignment: task.daysSinceAssignment,
                                        created_at: task.created_at,
                                        updated_at: task.updated_at
                                    }) : undefined}
                                >
                                    <div className="task-main-info">
                                        <div className="task-title-row">
                                            <span className="task-title">{task.title}</span>
                                            <Badge
                                                variant={task.status === 'in-progress' ? 'warning' : 'secondary'}
                                            >
                                                {task.status}
                                            </Badge>
                                        </div>
                                        <div className="task-meta">
                                            <span className="task-id">{task.displayId}</span>
                                            {task.feature_tag && (
                                                <ClickableFeatureTag
                                                    featureTag={task.feature_tag}
                                                    allFeatureTags={allFeatureTags}
                                                    allTasks={allTasks}
                                                    size="small"
                                                />
                                            )}
                                            {task.feature_tag?.due_date && (
                                                <TaskDueDate dueDate={task.feature_tag.due_date} />
                                            )}
                                        </div>
                                        <div className="task-footer">
                                            {task.lastWorkPlanDate && task.daysSinceLastWorkPlan !== null ? (
                                                <span className={`days-since work-plan-days ${getDaysColor(task.daysSinceLastWorkPlan)}`}>
                                                    {formatDays(task.daysSinceLastWorkPlan)}
                                                </span>
                                            ) : (
                                                <span className="days-since work-plan-days never">
                                                    Never
                                                </span>
                                            )}
                                            {task.notification && (
                                                <TaskNotifications
                                                    taskId={task.taskId}
                                                    taskTitle={task.title}
                                                    notification={task.notification}
                                                    isLoading={false}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        </div>
    );
};

export default UnpickedTasksByResource;

